import {observer} from "mobx-react";
import React, {useContext} from "react";
import {Grid} from "@material-ui/core";

import {green} from "@material-ui/core/colors";
import {useStores} from "../../../../../hooks";
import Stats from "./Stats";
import PageStoreContext from "../context";
import {
    AggregationPeriod,
    DataForGraph,
    Period,
    PeriodStats,
    StatsPeriodDataType,
    Title
} from "../../../../../services/stats/models";
import {getTimeSeriesStats} from "../../../../../services/stats";
import StatChart from "./StatChart";
import {StatFormType} from "../store/graphforms";
import {Trans, useTranslation} from "react-i18next";

const GeneralStatsBlock = () => {
  const {i18n, t} = useTranslation(["general", "stats"]);
  const locale = i18n.language;
  const { constantsStore } = useStores();
  const pageStore = useContext(PageStoreContext);

  let revenue,
    ordersAmount,
    averageCheck = "";
  let zRevenue,
    zOrdersAmount,
    zAverageCheck = "";

  let periodName: Period = pageStore.period.periodName;
  let formatter;

  let dataForGraph: DataForGraph[] = [{ time: "", total: 0 }];

  const amount = t("general:units.pieces");


  if (pageStore.generalStats && constantsStore.isReady) {
    formatter = constantsStore.getCurrencyFormatter(
      pageStore.generalStats.totalOrdersPrice.currencyId
    );
    revenue = formatter.toShowFormatted(
      pageStore.generalStats.totalOrdersPrice.price
    );
    averageCheck = formatter.toShowFormatted(
      pageStore.generalStats.averageOrderPrice.price
    );
    ordersAmount = pageStore.generalStats.ordersAmount;
  } else {
    revenue = "0";
    ordersAmount = "0";
    averageCheck = "0";
  }

  const countChangeStatisctics = (x: number, y: number) => {
    return (((x - y) / y) * 100).toFixed(2);
  };

  if (pageStore.prevStats) {
    let xRevenue: number = 0;
    let xAverageCheck: number = 0;
    let xOrdersAmount: number = 0;

    if (pageStore.generalStats) {
      xRevenue = pageStore.generalStats.totalOrdersPrice.price;
      xAverageCheck = pageStore.generalStats.averageOrderPrice.price;
      xOrdersAmount = pageStore.generalStats.ordersAmount;
    }

    let yPrevRevenue: number = pageStore.prevStats.totalOrdersPrice.price;
    let yPrevAverageCheck: number = pageStore.prevStats.averageOrderPrice.price;
    let yPrevOrdersAmount: number = pageStore.prevStats.ordersAmount;


    zRevenue = countChangeStatisctics(xRevenue, yPrevRevenue);
    zAverageCheck = countChangeStatisctics(xAverageCheck, yPrevAverageCheck);
    zOrdersAmount = countChangeStatisctics(xOrdersAmount, yPrevOrdersAmount);
  } else {
    if(revenue === "0"){
      zRevenue = "0";
      zOrdersAmount = "0";
      zAverageCheck = "0";
    }else{
      zRevenue = "100";
      zOrdersAmount = "100";
      zAverageCheck = "100";
    }
  }

  const showGraphic = () => {
    pageStore.formsStore.showPopupForm(StatFormType.drawGraph);
  };

  function makingTitleForGraphic(type: StatsPeriodDataType, t): string {
    switch (type) {
      case StatsPeriodDataType.revenueAmount:
        return t("stats:revenue");
      case StatsPeriodDataType.ordersAmount:
        return t("stats:ordersLittle");
      case StatsPeriodDataType.averageCheck:
        return t("stats:averageCheck");
      default:
        return "";
    }
  }

  const drawGraphic = async (type: StatsPeriodDataType) => {

    if (averageCheck === "0") {
      return;
    }

    pageStore.setIsLoadedGraphic(true);

    const from: Date = pageStore.from.toDate();

    const to: Date = pageStore.to.toDate();

    const aggregationTimeInterval = pageStore.period.aggregationTimeInterval;

    let isDifferentDate =
      from.getDay() !== to.getDay() ||
      from.getMonth() !== to.getMonth() ||
      from.getFullYear() !== to.getFullYear();

    let isDifferentTime = false;

    const res = await getTimeSeriesStats({
      aggregationTimeInterval: aggregationTimeInterval,
      categoryId: null,
      dataType: type,
      fromDate: from,
      itemId: null,
      toDate: to,
      menuId: pageStore.currentMenu.id,
    });
    if (!res.err) {
      let stats: PeriodStats[] = res.stats;
      let currencyId: string =
        type === StatsPeriodDataType.ordersAmount
          ? amount
          : res.currencyId
          ? res.currencyId
          : "";
      dataForGraph = stats.map((stat) => {
        let date = new Date(stat.toDate);
        let time = date.toLocaleTimeString(navigator.language, {
          hour: "2-digit",
          minute: "2-digit",
        });
        let resultDate = isDifferentDate
            ? `${time} ${date.toLocaleDateString()}`
            : isDifferentTime
            ? time
            : date.toLocaleDateString();

        return {
          time: resultDate,
          total: stat.total,
        };
      });
      pageStore.setGraphicType(type);
      pageStore.setSalesIn(currencyId);
      pageStore.setGraphTitle(makingTitleForGraphic(type, t));
      pageStore.setDataForGraph(dataForGraph);
      pageStore.setIsLoadedGraphic(false);
      showGraphic();
    }
  };

  return (
    <Grid container spacing={4}>
      <StatChart />
      <Grid item xs={12} sm={4} md={4} lg={4} xl={3}>
        <Stats
          title={t("stats:Title.Revenue")}
          amount={revenue}
          chip=""
          percentageText="+26%"
          percentagecolor={green[500]}
          showGraphic={() => drawGraphic(StatsPeriodDataType.revenueAmount)}
          prevAmount={zRevenue}
          periodName={periodName}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={3}>
        <Stats
          title={t("stats:Title.OrdersAmount")}
          amount={`${ordersAmount} ${amount}`}
          chip=""
          percentageText="+26%"
          percentagecolor={green[500]}
          showGraphic={() => drawGraphic(StatsPeriodDataType.ordersAmount)}
          prevAmount={zOrdersAmount}
          periodName={periodName}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={3}>
        <Stats
          title={t("stats:Title.AverageCheck")}
          amount={averageCheck}
          chip=""
          percentageText="+26%"
          percentagecolor={green[500]}
          showGraphic={() => drawGraphic(StatsPeriodDataType.averageCheck)}
          prevAmount={zAverageCheck}
          periodName={periodName}
        />
      </Grid>
    </Grid>
  );
};

export default observer(GeneralStatsBlock);
